<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-snackbar
          v-model="snackbar.visible"
          :color="snackbar.color"
          :timeout="snackbar.timeout"
          :top="snackbar.position === 'top'"
          max-width="400"
        >
          <v-layout align-center pr-4>
            <v-icon class="pr-3" dark large>{{ snackbar.icon }}</v-icon>
            <v-layout column>
              <div><strong>{{ snackbar.title }}</strong></div>
              <div>{{ snackbar.text }}</div>
            </v-layout>
          </v-layout>
          <template v-slot:action="{ attrs }">
            <v-btn icon v-bind="attrs" @click="snackbar.visible = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </template>
        </v-snackbar>
      </v-col>
    </v-row>

    <v-card class="mx-auto pa-4" elevation="2">
      <v-card-title class="d-flex align-center">
        <v-avatar color="primary" size="48" class="elevation-2 mr-4">
          <v-icon dark size="28">mdi-chart-bar</v-icon>
        </v-avatar>
        <div>
          <div class="text-h5 font-weight-bold primary--text">
            Progress Input KPI
          </div>
          <div class="text-caption grey--text text--darken-1">
            Monitoring progress pengisian KPI per user
          </div>
        </div>
      </v-card-title>

      <v-tabs v-model="activeTab" background-color="#125a97" dark>
        <v-tab>
          <v-icon left>mdi-table-eye</v-icon>
          Monitoring Pengisian
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <v-card flat>
            <v-card-text>
              <v-card outlined class="mt-2">
                <v-card-title>
                  <v-icon color="primary" class="mr-2">mdi-format-list-checks</v-icon>
                  {{ entityName || 'Data Pengisian' }}
                  <v-spacer></v-spacer>
                  <v-btn
                    color="success"
                    dark
                    small
                    :loading="exporting"
                    @click="exportExcel"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-microsoft-excel</v-icon>
                    Export Excel
                  </v-btn>
                </v-card-title>

                <div v-if="loading" class="text-center py-10">
                  <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
                  <div class="mt-3 grey--text">Memuat data...</div>
                </div>

                <div v-else-if="periodLabels.length === 0" class="text-center py-10">
                  <v-icon large color="grey lighten-1">mdi-table-off</v-icon>
                  <div class="mt-3 grey--text">Tidak ada data monitoring.</div>
                </div>

                <div v-else class="table-wrapper">
                  <table class="monitoring-table">
                    <thead>
                      <tr>
                        <th class="col-nama">Nama</th>
                        <th class="col-posisi">Posisi</th>
                        <th
                          v-for="(label, idx) in periodLabels"
                          :key="'h-' + idx"
                          class="col-month"
                        >
                          {{ label }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, rowIdx) in individualMonitoring"
                        :key="'row-' + rowIdx"
                      >
                        <td class="col-nama">{{ item.user_name || 'N/A' }}</td>
                        <td class="col-posisi">{{ item.position_name || '-' }}</td>
                        <td
                          v-for="(month, mIdx) in periodMonths"
                          :key="'cell-' + rowIdx + '-' + mIdx"
                          class="col-month"
                          :class="getCellClass(getPercentage(item.monthly_progress, month))"
                        >
                          {{ formatPercentage(getPercentage(item.monthly_progress, month)) }}
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr class="summary-row">
                        <td class="col-nama"></td>
                        <td class="col-posisi font-weight-bold">TOTAL PENGISIAN BISNIS UNIT</td>
                        <td
                          v-for="(month, mIdx) in periodMonths"
                          :key="'sum-' + mIdx"
                          class="col-month font-weight-bold"
                          :class="getCellClass(getSummaryPercentage(month))"
                        >
                          {{ formatPercentage(getSummaryPercentage(month)) }}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </v-card>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
export default {
	data() {
		return {
			activeTab: 0,
			loading: false,
			exporting: false,
			entityName: "",
			periodMonths: [],
			periodLabels: [],
			entityMonthlySummary: {},
			overallSummary: {},
			individualMonitoring: [],
			snackbar: {
				color: null,
				icon: null,
				mode: null,
				position: "top",
				text: null,
				timeout: 5000,
				title: null,
				visible: false,
			},
		};
	},
	computed: {
		entity() {
			return this.$store.state.entity;
		},
	},
	watch: {
		"entity.id": {
			async handler(newVal, oldVal) {
				setTimeout(() => {
					if (newVal && newVal !== oldVal) {
						this.$nextTick(() => {
							this.fetchProgress();
						});
					}
				}, 200);
			},
			immediate: false,
		},
	},
	mounted() {
		this.waitForTokenAndLoad();
	},
	methods: {
		async waitForTokenAndLoad() {
			let retries = 5;
			while (!this.$store.getters.isTokenUser && retries-- > 0) {
				await new Promise((resolve) => setTimeout(resolve, 300));
			}
			if (!this.$store.getters.isTokenUser) return;
			this.fetchProgress();
		},

		async fetchProgress() {
			this.loading = true;
			this.$store.dispatch("setOverlay", true);

			try {
				const params = {};
				if (this.entity?.id) {
					params.entity_id = this.entity.id;
				}

				const res = await this.$axios.get(
					`${process.env.VUE_APP_URL}/api/monitoring/pengisian`,
					{
						headers: {
							Authorization: `Bearer ${this.$store.getters.isTokenUser}`,
						},
						params,
					}
				);

				const data = res.data;

				this.entityName = data.entity_name || "";
				this.periodMonths = data.period_months || [];
				this.periodLabels = data.period_labels || [];
				this.entityMonthlySummary = data.entity_monthly_summary || {};
				this.overallSummary = data.overall_summary || {};
				this.individualMonitoring = data.individual_monitoring || [];
			} catch (err) {
				console.error(
					"Gagal memuat data monitoring:",
					err?.response?.data?.message || err?.message || err
				);
				this.snackbar = {
					color: "error",
					icon: "mdi-alert-circle-outline",
					mode: "snackbar",
					position: "top",
					text:
						err?.response?.data?.message ||
						"Gagal memuat data monitoring. Silakan coba lagi.",
					timeout: 5000,
					title: null,
					visible: true,
				};
			} finally {
				this.loading = false;
				this.$store.dispatch("setOverlay", false);
			}
		},

		async exportExcel() {
			this.exporting = true;
			try {
				const params = {};
				if (this.entity?.id) {
					params.entity_id = this.entity.id;
				}

				const res = await this.$axios.get(
					`${process.env.VUE_APP_URL}/api/monitoring/pengisian/excel`,
					{
						headers: {
							Authorization: `Bearer ${this.$store.getters.isTokenUser}`,
						},
						params,
						responseType: "blob",
					}
				);

				const blob = new Blob([res.data], {
					type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
				});
				const url = globalThis.URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute(
					"download",
					`Progress_Input_KPI_${this.entityName || "Report"}.xlsx`
				);
				document.body.appendChild(link);
				link.click();
				link.remove();
				globalThis.URL.revokeObjectURL(url);

				this.snackbar = {
					color: "success",
					icon: "mdi-check-circle",
					mode: "snackbar",
					position: "top",
					text: "Export Excel berhasil!",
					timeout: 3000,
					title: null,
					visible: true,
				};
			} catch (err) {
				console.error(
					"Gagal export excel:",
					err?.response?.data?.message || err?.message || err
				);
				this.snackbar = {
					color: "error",
					icon: "mdi-alert-circle-outline",
					mode: "snackbar",
					position: "top",
					text:
						err?.response?.data?.message ||
						"Gagal export excel. Silakan coba lagi.",
					timeout: 5000,
					title: null,
					visible: true,
				};
			} finally {
				this.exporting = false;
			}
		},

		getPercentage(monthlyProgress, month) {
			if (!monthlyProgress || !monthlyProgress[month]) return null;
			const p = monthlyProgress[month].percentage;
			return p == null ? null : p;
		},

		getSummaryPercentage(month) {
			if (!this.entityMonthlySummary || !this.entityMonthlySummary[month])
				return null;
			const p = this.entityMonthlySummary[month].percentage;
			return p == null ? null : p;
		},

		formatPercentage(value) {
			if (value == null) return "-";
			if (value === 0) return "0%";
			if (Number.isInteger(value)) return value + "%";
			return Math.round(value) + "%";
		},

		getCellClass(percentage) {
			if (percentage == null) return "cell-grey";
			if (percentage >= 100) return "cell-green";
			if (percentage > 0) return "cell-yellow";
			return "cell-red";
		},
	},
};
</script>

<style scoped>
.table-wrapper {
	overflow-x: auto;
	padding: 0;
}

.monitoring-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
	min-width: 900px;
}

.monitoring-table thead th {
	background-color: #2d4f6c;
	color: #fff;
	padding: 8px 10px;
	text-align: center;
	font-weight: 600;
	white-space: nowrap;
	border: 1px solid #ddd;
	position: sticky;
	top: 0;
	z-index: 1;
}

.monitoring-table tbody td,
.monitoring-table tfoot td {
	padding: 6px 10px;
	border: 1px solid #ddd;
	text-align: center;
	white-space: nowrap;
}

.monitoring-table .col-nama {
	text-align: left;
	min-width: 140px;
	max-width: 200px;
	white-space: normal;
	word-break: break-word;
}

.monitoring-table .col-posisi {
	text-align: left;
	min-width: 150px;
	max-width: 250px;
	white-space: normal;
	word-break: break-word;
}

.monitoring-table .col-month {
	min-width: 65px;
}

.monitoring-table tbody tr:hover {
	background-color: #f5f5f5;
}

.monitoring-table .summary-row {
	background-color: #f0f0f0;
}

.monitoring-table .summary-row td {
	font-weight: 700;
}

/* Cell colors based on percentage */
.cell-green {
	background-color: #a5d6a7 !important;
	color: #1b5e20;
	font-weight: 600;
}

.cell-yellow {
	background-color: #fff176 !important;
	color: #5d4037;
	font-weight: 600;
}

.cell-red {
	background-color: #ef9a9a !important;
	color: #4e0000;
	font-weight: 600;
}

.cell-grey {
	background-color: #e0e0e0 !important;
	color: #757575;
}
</style>
